import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, ClearableInput, Spinner } from '@rio-cloud/rio-uikit';
import { NotificationUserFormData } from '../../../../../models/notificationUser';
import { fetchUser } from '../../../../../api/fetchUser';

interface NotificationUserFormProps {
  addNotificationUser: (data: NotificationUserFormData) => Promise<boolean>;
  onSuccess: () => void;
  onCancel: () => void;
}

const NotificationUserForm: React.FC<NotificationUserFormProps> = ({
  addNotificationUser,
  onSuccess,
  onCancel,
}) => {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<NotificationUserFormData>({
    userId: '',
    firstName: '',
    lastName: '',
    email: '',
  });

  const handleUserIdChange = async (value: string) => {
    setFormData(prev => ({
      ...prev,
      userId: value,
    }));

    if (errors.userId) {
      setErrors(prev => ({
        ...prev,
        userId: '',
      }));
    }

    // Se o userId foi preenchido, buscar o usuário
    if (value.trim()) {
      setLoadingUser(true);
      try {
        const user = await fetchUser(value.trim());
        if (user) {
          setFormData(prev => ({
            ...prev,
            userId: value.trim(),
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
          }));
        } else {
          setErrors(prev => ({
            ...prev,
            userId: intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserForm.userNotFound' }),
          }));
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        setErrors(prev => ({
          ...prev,
          userId: intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserForm.userFetchError' }),
        }));
      } finally {
        setLoadingUser(false);
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.userId.trim()) {
      newErrors.userId = intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserForm.userIdRequired' });
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserForm.firstNameRequired' });
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserForm.lastNameRequired' });
    }

    if (!formData.email.trim()) {
      newErrors.email = intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserForm.emailRequired' });
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserForm.emailInvalid' });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const success = await addNotificationUser(formData);

      if (success) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof NotificationUserFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="text-size-h3">{intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserForm.userData' })}</div>
        <div>
          <div>
            <label htmlFor="userId" className="block font-medium text-gray-700 dark:text-neutral-300 mb-2">
              {intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserForm.userIdLabel' })}
            </label>
            <div className="relative">
              <ClearableInput
                id="userId"
                value={formData.userId}
                onChange={handleUserIdChange}
                placeholder={intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserForm.userIdPlaceholder' })}
                disabled={loadingUser}
              />
              {loadingUser && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Spinner />
                </div>
              )}
            </div>
            {errors.userId && (
              <p className="text-red-600 text-sm mt-1">{errors.userId}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="firstName" className="block font-medium text-gray-700 dark:text-neutral-300 mb-2">
            {intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserForm.firstNameLabel' })}
          </label>
          <ClearableInput
            id="firstName"
            value={formData.firstName}
            onChange={(value) => handleInputChange('firstName', value)}
            placeholder={intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserForm.firstNamePlaceholder' })}
            disabled={true}
          />
          {errors.firstName && (
            <p className="text-red-600 text-sm mt-1">{errors.firstName}</p>
          )}
        </div>

        <div>
          <label htmlFor="lastName" className="block font-medium text-gray-700 dark:text-neutral-300 mb-2">
            {intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserForm.lastNameLabel' })}
          </label>
          <ClearableInput
            id="lastName"
            value={formData.lastName}
            onChange={(value) => handleInputChange('lastName', value)}
            placeholder={intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserForm.lastNamePlaceholder' })}
            disabled={true}
          />
          {errors.lastName && (
            <p className="text-red-600 text-sm mt-1">{errors.lastName}</p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="block font-medium text-gray-700 dark:text-neutral-300 mb-2">
            {intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserForm.emailLabel' })}
          </label>
          <ClearableInput
            id="email"
            value={formData.email}
            onChange={(value) => handleInputChange('email', value)}
            placeholder={intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserForm.emailPlaceholder' })}
            disabled={true}
          />
          {errors.email && (
            <p className="text-red-600 text-sm mt-1">{errors.email}</p>
          )}
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-6">
        <Button
          bsStyle={Button.DEFAULT}
          onClick={onCancel}
          disabled={loading}
        >
          {intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserForm.cancel' })}
        </Button>
        <Button
          bsStyle={Button.PRIMARY}
          type="submit"
          disabled={loading || loadingUser}
          iconName={loading ? undefined : "rioglyph-check"}
        >
          {loading ? (
            <div className="flex items-center">
              <Spinner />
              {intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserForm.adding' })}
            </div>
          ) : (
            intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserForm.add' })
          )}
        </Button>
      </div>
    </form>
  );
};

export default NotificationUserForm;

